/**
 * Build-time configuration, from the 20-api stack outputs.
 *
 * None of these is a secret. A Cognito user pool id and a public app client
 * id are meant to live in a browser; PKCE is what protects the flow. There is
 * deliberately nothing here that could become one -- if a value ever needs
 * keeping, it does not belong in a bundle that ships to a child's tablet.
 */

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set. Copy apps/web/.env.example to .env.local and fill ` +
        `it in from the 20-api stack outputs.`,
    );
  }
  return value;
}

export const config = {
  apiBase: required('VITE_API_BASE', import.meta.env.VITE_API_BASE),
  cognito: {
    domain: required('VITE_COGNITO_DOMAIN', import.meta.env.VITE_COGNITO_DOMAIN),
    clientId: required('VITE_COGNITO_CLIENT_ID', import.meta.env.VITE_COGNITO_CLIENT_ID),
    region: required('VITE_COGNITO_REGION', import.meta.env.VITE_COGNITO_REGION),
    userPoolId: required('VITE_COGNITO_USER_POOL_ID', import.meta.env.VITE_COGNITO_USER_POOL_ID),
    /**
     * Whether the pool has Google as an identity provider (the GoogleSignIn
     * output of 20-api). Off unless the stack says so, so a build against a
     * pool without Google never shows a button that can only fail. Google is
     * reached only through Cognito's own domain -- no Google script or
     * request ever leaves this app, so allowedOrigins does not change.
     */
    google: import.meta.env.VITE_COGNITO_GOOGLE === 'true',
    /** Sign in with Apple (the AppleSignIn output of 20-api). Same rules. */
    apple: import.meta.env.VITE_COGNITO_APPLE === 'true',
  },
} as const;

/**
 * Every origin this app is allowed to talk to.
 *
 * The audit the brief asks for is not a document written at the end -- it is
 * this list, and `assertKnownOrigin` below, which throws if anything tries to
 * reach somewhere that is not on it. A new entry here should have to be
 * argued for in review.
 */
export const allowedOrigins: readonly string[] = [
  new URL(config.apiBase).origin,
  new URL(config.cognito.domain).origin,
  `https://cognito-idp.${config.cognito.region}.amazonaws.com`,
];

export function assertKnownOrigin(url: string): void {
  const origin = new URL(url, window.location.origin).origin;
  if (origin === window.location.origin) return;
  if (!allowedOrigins.includes(origin)) {
    throw new Error(
      `Blocked a request to ${origin}. Birbly talks to its own API and to ` +
        `Cognito, and to nothing else. If this is deliberate, it belongs in ` +
        `allowedOrigins in src/config.ts with a reason.`,
    );
  }
}
